// ──────────────────────────────────────────────
// Grading Engine – Core Logic
// Deterministic, rule-based grading.
// ──────────────────────────────────────────────

import type {
  Answer,
  FullGradingResult,
  GradeCategory,
  MultiSelectAnswer,
  PolicyAnswer,
  PolicyQuestion,
  Question,
  QuestionGradingResult,
  Questionnaire,
  ScaleAnswer,
  ScaleQuestion,
  ValidationError,
} from "./gradingEngine.types";

// ─── Scale Grading ───────────────────────────

/**
 * Compares two scale answers by looking up numeric values from option IDs.
 *
 * Rules:
 * - Same value        → alignment
 * - Difference = 1    → discussion
 * - Difference ≥ 2    → key_decision
 *
 * @throws if either option ID is not found in the question definition.
 */
export function gradeScale(
  selectedOptionIdA: string,
  selectedOptionIdB: string,
  question: ScaleQuestion
): GradeCategory {
  const optA = question.options.find((o) => o.id === selectedOptionIdA);
  const optB = question.options.find((o) => o.id === selectedOptionIdB);

  if (!optA) throw new Error(`Scale option ID "${selectedOptionIdA}" not found in question "${question.id}"`);
  if (!optB) throw new Error(`Scale option ID "${selectedOptionIdB}" not found in question "${question.id}"`);

  const diff = Math.abs(optA.value - optB.value);

  if (diff === 0) return "alignment";
  if (diff === 1) return "discussion";
  return "key_decision";
}

// ─── Opposite Check ──────────────────────────

/**
 * Returns `true` if the two selected option IDs belong to declared
 * opposite groups on the given policy question.
 *
 * Checks both directions: (A in group_a & B in group_b) OR (A in group_b & B in group_a).
 */
export function checkOpposite(selectedOptionIdA: string, selectedOptionIdB: string, question: PolicyQuestion): boolean {
  if (!question.opposite_groups || question.opposite_groups.length === 0) {
    return false;
  }

  const optionA = question.options.find((o) => o.id === selectedOptionIdA);
  const optionB = question.options.find((o) => o.id === selectedOptionIdB);

  if (!optionA?.opposite_group || !optionB?.opposite_group) {
    return false;
  }

  const groupA = optionA.opposite_group;
  const groupB = optionB.opposite_group;

  return question.opposite_groups.some(
    (pair) =>
      (pair.group_a === groupA && pair.group_b === groupB) || (pair.group_a === groupB && pair.group_b === groupA)
  );
}

// ─── Policy Grading ──────────────────────────

/**
 * Compares two policy (single-select) answers.
 *
 * Rules (in priority order):
 * 1. Opposites                           → key_decision  (overrides everything)
 * 2. Same option                         → alignment
 * 3. Either option has is_flexible=true  → discussion
 * 4. Otherwise                           → discussion
 */
export function gradePolicy(
  selectedOptionIdA: string,
  selectedOptionIdB: string,
  question: PolicyQuestion
): GradeCategory {
  // 1. Opposite check – highest priority
  if (checkOpposite(selectedOptionIdA, selectedOptionIdB, question)) {
    return "key_decision";
  }

  // 2. Same option
  if (selectedOptionIdA === selectedOptionIdB) {
    return "alignment";
  }

  // 3. Flexibility check
  const optionA = question.options.find((o) => o.id === selectedOptionIdA);
  const optionB = question.options.find((o) => o.id === selectedOptionIdB);

  if (optionA?.is_flexible || optionB?.is_flexible) {
    return "discussion";
  }

  // 4. Different options, no flexibility, no opposites
  return "discussion";
}

// ─── Multi-Select Grading ────────────────────

/**
 * Compares two multi-select answers by computing overlap.
 *
 * Overlap = |intersection| / |union|
 *
 * Rules:
 * - overlap >= 0.5            → alignment
 * - overlap > 0 && < 0.5     → discussion
 * - overlap === 0             → key_decision
 *
 * Edge case: if both selections are empty, treated as alignment (identical empty sets).
 */
export function gradeMultiSelect(selectedIdsA: string[], selectedIdsB: string[]): GradeCategory {
  const setA = new Set(selectedIdsA);
  const setB = new Set(selectedIdsB);

  // Edge case: both empty → identical (alignment)
  if (setA.size === 0 && setB.size === 0) {
    return "alignment";
  }

  const intersection = new Set([...setA].filter((id) => setB.has(id)));
  const union = new Set([...setA, ...setB]);

  const overlapPct = intersection.size / union.size;

  if (overlapPct === 0) return "key_decision";
  if (overlapPct >= 0.5) return "alignment";
  return "discussion";
}

// ─── Single Question Dispatcher ──────────────

/**
 * Grades a single question by dispatching to the correct type-specific grader.
 *
 * @throws if question type is unrecognized or answer shape doesn't match.
 */
export function gradeQuestion(question: Question, answerA: Answer, answerB: Answer): GradeCategory {
  switch (question.type) {
    case "scale": {
      const a = answerA as ScaleAnswer;
      const b = answerB as ScaleAnswer;
      return gradeScale(a.selectedOptionId, b.selectedOptionId, question);
    }

    case "policy": {
      const a = answerA as PolicyAnswer;
      const b = answerB as PolicyAnswer;
      return gradePolicy(a.selectedOptionId, b.selectedOptionId, question);
    }

    case "multi_select": {
      const a = answerA as MultiSelectAnswer;
      const b = answerB as MultiSelectAnswer;
      return gradeMultiSelect(a.selectedOptionIds, b.selectedOptionIds);
    }

    default: {
      const _exhaustive: never = question;
      throw new Error(`Unknown question type: ${(_exhaustive as unknown as Question).type}`);
    }
  }
}

// ─── Helpers ─────────────────────────────────

/**
 * Extracts all questions from a sectioned questionnaire into a flat array
 * of `{ sectionId, question }` tuples.
 */
export function getAllQuestions(questionnaire: Questionnaire): { sectionId: string; question: Question }[] {
  const result: { sectionId: string; question: Question }[] = [];
  for (const section of questionnaire.sections) {
    for (const question of section.questions) {
      result.push({ sectionId: section.id, question });
    }
  }
  return result;
}

// ─── Full Questionnaire Grading ──────────────

/**
 * Grades every question in a questionnaire by comparing two parties' answers.
 * Iterates through all sections and their questions.
 *
 * **Clarification rule**: If either party has not answered a required question,
 * that question is automatically categorized as `"clarification"`.
 *
 * @param questionnaire - The questionnaire definition (with sections).
 * @param answersA - Map of questionId → Answer for party A (requestor).
 * @param answersB - Map of questionId → Answer for party B (requestee).
 * @returns Full grading result with per-question categories and summary counts.
 */
export function gradeFullQuestionnaire(
  questionnaire: Questionnaire,
  answersA: Record<string, Answer>,
  answersB: Record<string, Answer>
): FullGradingResult {
  const results: QuestionGradingResult[] = [];

  const summary = {
    alignment: 0,
    discussion: 0,
    key_decision: 0,
    clarification: 0,
    total: 0,
  };

  const allQuestions = getAllQuestions(questionnaire);

  for (const { sectionId, question } of allQuestions) {
    const ansA = answersA[question.id];
    const ansB = answersB[question.id];

    if (!ansA || !ansB) {
      results.push({
        questionId: question.id,
        questionText: question.text,
        questionType: question.type,
        sectionId,
        category: "clarification",
        answerA: ansA ?? ({ questionId: question.id } as Answer),
        answerB: ansB ?? ({ questionId: question.id } as Answer),
      });
      summary.clarification++;
      summary.total++;
      continue;
    }

    const category = gradeQuestion(question, ansA, ansB);

    results.push({
      questionId: question.id,
      questionText: question.text,
      questionType: question.type,
      sectionId,
      category,
      answerA: ansA,
      answerB: ansB,
    });

    summary[category]++;
    summary.total++;
  }

  return {
    questionnaireId: questionnaire.id,
    questionnaireTitle: questionnaire.title,
    results,
    summary,
  };
}

// ─── Questionnaire Validation ────────────────

/**
 * Validates structural integrity of a questionnaire definition.
 *
 * Checks:
 * 1. Every section has a unique ID.
 * 2. Every question has a unique ID (across all sections).
 * 3. Every question has `required` set.
 * 4. Scale options all have `id` and unique `value`.
 * 5. Policy / multi-select options all have unique `id`.
 * 6. If `opposite_groups` are defined on a policy question, every referenced
 *    group tag must exist on at least one option's `opposite_group` field.
 * 7. Every option whose `opposite_group` is set must be referenced by a
 *    declared `opposite_groups` pair.
 *
 * @returns Array of validation errors (empty = valid).
 */
export function validateQuestionnaire(questionnaire: Questionnaire): ValidationError[] {
  const errors: ValidationError[] = [];
  const seenQuestionIds = new Set<string>();
  const seenSectionIds = new Set<string>();

  for (const section of questionnaire.sections) {
    if (seenSectionIds.has(section.id)) {
      errors.push({ questionId: section.id, message: `Duplicate section ID: "${section.id}"` });
    }
    seenSectionIds.add(section.id);

    for (const question of section.questions) {
      if (seenQuestionIds.has(question.id)) {
        errors.push({ questionId: question.id, message: `Duplicate question ID: "${question.id}"` });
      }
      seenQuestionIds.add(question.id);

      if (question.required === undefined || question.required === null) {
        errors.push({ questionId: question.id, message: `Missing "required" field on question "${question.id}"` });
      }

      switch (question.type) {
        case "scale": {
          const optionIds = new Set<string>();
          const optionValues = new Set<number>();
          for (const opt of question.options) {
            if (!opt.id) {
              errors.push({
                questionId: question.id,
                message: `Scale option missing "id" field (label: "${opt.label}")`,
              });
            }
            if (optionIds.has(opt.id)) {
              errors.push({ questionId: question.id, message: `Duplicate scale option ID: "${opt.id}"` });
            }
            optionIds.add(opt.id);
            if (optionValues.has(opt.value)) {
              errors.push({ questionId: question.id, message: `Duplicate scale option value: ${opt.value}` });
            }
            optionValues.add(opt.value);
          }
          break;
        }

        case "policy": {
          const optionIds = new Set<string>();
          for (const opt of question.options) {
            if (optionIds.has(opt.id)) {
              errors.push({ questionId: question.id, message: `Duplicate policy option ID: "${opt.id}"` });
            }
            optionIds.add(opt.id);
          }

          if (question.opposite_groups && question.opposite_groups.length > 0) {
            const declaredGroups = new Set<string>();
            for (const pair of question.opposite_groups) {
              declaredGroups.add(pair.group_a);
              declaredGroups.add(pair.group_b);
            }

            const optionGroups = new Set<string>();
            for (const opt of question.options) {
              if (opt.opposite_group) {
                optionGroups.add(opt.opposite_group);
              }
            }

            for (const group of declaredGroups) {
              if (!optionGroups.has(group)) {
                errors.push({
                  questionId: question.id,
                  message: `Opposite group "${group}" is declared in opposite_groups but no option references it`,
                });
              }
            }

            for (const opt of question.options) {
              if (opt.opposite_group && !declaredGroups.has(opt.opposite_group)) {
                errors.push({
                  questionId: question.id,
                  message: `Option "${opt.id}" has opposite_group "${opt.opposite_group}" but it is not referenced in any opposite_groups pair`,
                });
              }
            }
          }
          break;
        }

        case "multi_select": {
          const optionIds = new Set<string>();
          for (const opt of question.options) {
            if (optionIds.has(opt.id)) {
              errors.push({ questionId: question.id, message: `Duplicate multi_select option ID: "${opt.id}"` });
            }
            optionIds.add(opt.id);
          }
          break;
        }
      }
    }
  }

  return errors;
}
